import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { colors } from '../theme/colors';
import { subscribeBusy } from '../utils/busyBus';

/**
 * One app-wide loading overlay driven by busyBus. It is triggered automatically
 * whenever a transaction is created, edited, or deleted (wired in
 * transactionHelper), so EVERY form gets a consistent, professional loader with
 * zero per-form code. It also blocks pointer events while active, which prevents
 * accidental double-clicks (the duplicate-transaction problem) during the save.
 */
export default function GlobalBusyOverlay() {
  const [state, setState] = useState({ active: false, label: '' });
  useEffect(() => subscribeBusy(setState), []);

  if (!state.active) return null;
  return (
    <Overlay role="alertdialog" aria-busy="true" aria-live="assertive">
      <Card>
        <Spinner />
        <Label>{state.label || 'Please wait…'}</Label>
        <Sub>Please don't close or refresh</Sub>
      </Card>
    </Overlay>
  );
}

const spin = keyframes`to { transform: rotate(360deg); }`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100000;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  /* Swallows all clicks/taps while a save/delete is in flight. */
  pointer-events: all;
  cursor: progress;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: 28px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  box-shadow: 0 12px 44px rgba(0, 0, 0, 0.28);
  min-width: 220px;
`;

const Spinner = styled.div`
  width: 46px;
  height: 46px;
  border: 4px solid ${colors?.background?.paper || '#eee'};
  border-top-color: ${colors?.primary?.main || '#800000'};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const Label = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${colors?.text?.primary || '#222'};
`;

const Sub = styled.div`
  font-size: 12px;
  color: ${colors?.text?.secondary || '#888'};
`;
